import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Col, Row, Layout, Space, Button } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import { EditOutlined, WarningOutlined } from '@ant-design/icons';
import Avatar from '../avatar';
import QuestionCard from '../question_card';
import AnswerFooter from '../answer_footer';
import UserResponse from '../user_response';
import FeedbackSurvey from '../feedback_survey';
import InteractionsExpirationDialog from '../interaction_expiration_dialog';
import {
  answeredFeedbackQuestionsAtom,
  currentFeedbackQuestionIndexAtom,
  currentQuestionIndexAtom,
  feedbackSurveyCompleteAtom,
  interactionIdAtom,
  decodedTokenAtom,
  singleSelectResponseAtom,
  updateFeedbackSurveyAtom,
  updateSurveyAtom,
} from '../../store';
import { useAtom } from 'jotai';
import { ClearQuestionsModal } from '../modals';
import {
  QuestionnaireData,
  QuestionType,
  UserResponseType,
} from '../../models/questionnaire.interface';
import {
  CreateInteractionPostType,
  CreateInteractionResponseType,
  CreateStaffInteractionPostType,
  GetInteractionResponseDataType,
  InteractionEditStatusEnum,
  InteractionStatusEnum,
  InteractionUserResponses,
  UpdateInteractionPatchType,
  UpdateInteractionResponseType,
  ViewLogActionEnum,
  ViewLogQuestion,
} from '../../models/interactions.interface';
import {
  FeedbackQuestionType,
  FeedbackSurveyDataType,
} from '../../models/feedback_survey.interface';
import EndOfSurveyMessage from 'components/end_of_survey_message';
import ErrorAlert from 'components/error_alert';
import RetrySavingModal from 'components/modals/retry_saving_modal';
import { api } from 'api/hooks/api';
import { COLORS } from 'themes/colors';

const Questionnaire: React.FC<{
  questionnaireData: QuestionnaireData;
  token: string;
  feedbackSurveyData: FeedbackSurveyDataType | null;
}> = ({ questionnaireData, token, feedbackSurveyData }) => {
  const [interactionId, setInteractionId] = useAtom(interactionIdAtom);
  const [decodedToken] = useAtom(decodedTokenAtom);
  const patientId = decodedToken?.patientId;
  const staffMemberId = decodedToken?.staffMemberId;
  const isStaffInteraction = !!staffMemberId;
  const [interaction, setInteraction] = useAtom(updateSurveyAtom);

  // Debounce helper for smooth scrolling to prevent ResizeObserver issues
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedScrollIntoView = (element: HTMLElement | null) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      element?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const config = { headers: { Authorization: `Bearer ${token}` } };
  const [searchParams, setSearchParams] = useSearchParams();
  const encounterId = searchParams.get('encounterId');
  const unitId = searchParams.get('unitId');

  // Retrieve interactionId from URL if it exists
  const resumableInteractionId
    = searchParams.get('interactionId') != null
      ? Number(searchParams.get('interactionId'))
      : null;

  const [errorLoadingData, setErrorLoadingData] = useState(false);

  const getExistingInteraction = api<void, GetInteractionResponseDataType>(
    'get',
    isStaffInteraction
      ? `staff_interactions/${resumableInteractionId}`
      : `interactions/${resumableInteractionId}`,
    config,
  );
  const createPatientInteraction = api<
    CreateInteractionPostType,
    CreateInteractionResponseType
  >('post', 'interactions', config);
  const createStaffInteraction = api<
    CreateStaffInteractionPostType,
    CreateInteractionResponseType
  >('post', 'staff_interactions', config);

  // Retrieve the last incomplete interaction if it exists
  // Create new interaction if no interactionId is present
  // Set interactionId from existing or new interaction
  useEffect(() => {
    const fetchData = async () => {
      // Fetch the data
      let fetchedInteraction: UpdateInteractionPatchType & { id: number };
      if (!interactionId && resumableInteractionId) {
        const result = await getExistingInteraction();
        fetchedInteraction = {
          id: resumableInteractionId,
          questionViewLog: result.questionViewLog || [],
          expired: result.expired ?? false,
          editStatus:
            result.expired || result.status == InteractionStatusEnum.completed
              ? InteractionEditStatusEnum.closed
              : InteractionEditStatusEnum.open,
          responses: result.responses || [],
        };

        if (result.status == InteractionStatusEnum.completed) {
          setWasLoadedClosed(true);
          completeQuestionnaire();
        }
        else if (result.expired) {
          setInteractionExpirationDialog(true);
        }
      }
      else {
        let createResult: CreateInteractionResponseType;
        if (isStaffInteraction) {
          if (!staffMemberId) {
            setErrorLoadingData(true);
            return;
          }
          createResult = await createStaffInteraction({
            scriptId: questionnaireData.id,
            staffMemberId,
          });
        }
        else {
          if (!patientId || !encounterId || !unitId) {
            setErrorLoadingData(true);
            return;
          }
          createResult = await createPatientInteraction({
            scriptId: questionnaireData.id,
            patientId,
            encounterId,
            unitId,
          });
        }
        fetchedInteraction = {
          id: createResult.id,
          questionViewLog: [],
          expired: false,
          editStatus: InteractionEditStatusEnum.open,
          responses: [],
        };
      }
      // Update the state
      setInteraction(fetchedInteraction);
      setInteractionId(fetchedInteraction.id);
      updateInteractionIdInURL(fetchedInteraction.id);

      if (
        fetchedInteraction.responses
        && fetchedInteraction.responses.length > 0
      ) {
        const lastResponse
          = fetchedInteraction.responses[fetchedInteraction.responses.length - 1];

        const index = questions.findIndex(
          question => lastResponse.questionId === question.id,
        );
        setCurrentQuestionIndex(index);
      }

      setIsLoading(false);
    };

    fetchData().catch(() => {
      setErrorLoadingData(true);
    });

    return () => {
      // Cleanup logic here
      setInteraction(null);
    };
  }, []);

  const { questions: questionnaireDataWithStaffRec } = questionnaireData;
  const ref = useRef<null | HTMLDivElement>(null);
  const currentQuestionRef = useRef<null | HTMLDivElement>(null);
  const surveyCompletedRef = useRef<null | HTMLDivElement>(null);
  // Staff recognition has no survey UI (CIP-3136). Caregram UI lands in CIP-10081,
  // so hide that type until it can be answered.
  const questions = questionnaireDataWithStaffRec.filter(
    (question: any) => question.type !== 'staffRecognition' && question.type !== 'caregram',
  );
  const [singleSelectResponse, setSingleSelectResponse] = useAtom(
    singleSelectResponseAtom,
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useAtom(
    currentQuestionIndexAtom,
  );
  const [feedbackSurveyComplete] = useAtom(feedbackSurveyCompleteAtom);

  const [hideAnswerFooter, setHideAnswerFooter] = useState(false);

  const [editQuestionIndex, setEditQuestionIndex] = useState(0);
  const currentQuestion = questions[currentQuestionIndex];
  const [editQuestionId, setEditQuestionId] = useState(0);
  const [editQuestionType, setEditQuestionType] = useState('');
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const [clearQuestions, setClearQuestions] = useState(false);
  const [endOfQuestionnaire, setEndOfQuestionnaire] = useState(false);
  const [displayFeedbackSurvey, setDisplayFeedbackSurvey] = useState(false);
  const [disableEditButtons, setDisableEditButtons] = useState(false);
  const [updatedFeedbackSurveyResponse, setUpdatedFeedbackSurveyResponse]
    = useAtom(updateFeedbackSurveyAtom);
  const [currentFeedbackQuestionIndex] = useAtom(
    currentFeedbackQuestionIndexAtom,
  );
  const [answeredFeedbackQuestions, setAnsweredFeedbackQuestions] = useAtom(
    answeredFeedbackQuestionsAtom,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isRestarting, setIsRestarting] = useState(false);
  const [wasLoadedClosed, setWasLoadedClosed] = useState(false);

  const questionsById = useMemo(() => {
    return questionnaireData.questions.reduce((acc, question) => {
      acc.set(question.id, question);
      return acc;
    }, new Map<number, QuestionType>());
  }, [questionnaireData]);

  const feedbackQuestionsById = useMemo(() => {
    return (feedbackSurveyData?.questions || []).reduce((acc, question) => {
      acc.set(question.id, question);
      return acc;
    }, new Map<number, FeedbackQuestionType>());
  }, [feedbackSurveyData]);

  const unlockedUuids = useMemo(() => {
    const unlockingUuids = new Set<string>();
    interaction?.responses?.map((response) => {
      const question = questionsById.get(response.questionId);

      (response.answers ?? []).forEach((ans) => {
        const qans = question?.answers.find(x => x.id === ans.id);
        (qans?.unlocksQuestionUuids ?? []).forEach((item: any) =>
          unlockingUuids.add(item),
        );
      });
    });
    return unlockingUuids;
  }, [interaction]);

  const execUpdateInteraction = api<
    UpdateInteractionPatchType,
    UpdateInteractionResponseType
  >('patch', isStaffInteraction ? `staff_interactions/${interactionId}` : `interactions/${interactionId}`, config);

  const [savingAnswerIndex, setSavingAnswerIndex] = useState<number | null>(
    null,
  );

  const [savingFailed, setSavingFailed] = useState(false);

  const updateInteractionIdInURL = (interactionId: number) => {
    // Avoid polluting browser history with duplicate entries on refresh.
    const currentParam = searchParams.get('interactionId');
    const newValue = String(interactionId);
    if (currentParam === newValue) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('interactionId', newValue);
    // Replace instead of push so back button works intuitively.
    setSearchParams(newParams, { replace: true });
  };

  // Every time the interaction is updated, we want to scroll to the current answer.
  useEffect(() => {
    if (isLoading) return;

    if (ref?.current && interaction!.responses.length && !isBeingEdited) {
      debouncedScrollIntoView(ref.current);
    }
  }, [interaction, isLoading]);

  const [interactionUpdatesQueue, setInteractionUpdatesQueue] = useState(
    new Array<{
      interactionParam: UpdateInteractionPatchType;
      savingAnswerIndex: number | null;
    }>(),
  );

  const [interactionRequestRunning, setInteractionRequestRunning]
    = useState(false);
  const [hasQueuedClosedStatus, setHasQueuedClosedStatus] = useState(false);

  const queueInteractionUpdate = () => {
    if (!isLoading && interaction) {
      // If the interaction was loaded in a closed state, don't queue any more updates.
      // This prevents duplicate PATCH requests when refreshing after completion.
      if (wasLoadedClosed) {
        return;
      }

      // Prevent queueing the closed status more than once during an active completion flow
      if (interaction.editStatus === InteractionEditStatusEnum.closed && hasQueuedClosedStatus) {
        return;
      }

      if (interaction.editStatus === InteractionEditStatusEnum.closed) {
        setHasQueuedClosedStatus(true);
      }

      const newQueue = new Array(...interactionUpdatesQueue);
      newQueue.push({ interactionParam: interaction, savingAnswerIndex });
      setInteractionUpdatesQueue(newQueue);
    }
  };

  useEffect(() => {
    if (!interactionRequestRunning && interactionUpdatesQueue.length) {
      popInteractionUpdate();
    }
  }, [interactionUpdatesQueue, interactionRequestRunning]);

  const popInteractionUpdate = async () => {
    try {
      const update = interactionUpdatesQueue.shift();
      setInteractionUpdatesQueue(new Array(...interactionUpdatesQueue));
      if (update) {
        setInteractionRequestRunning(true);
        await updateInteraction(update);
      }
    }
    finally {
      setInteractionRequestRunning(false);
    }
  };

  const updateInteraction = async ({
    interactionParam,
    savingAnswerIndex,
  }: {
    interactionParam: UpdateInteractionPatchType;
    savingAnswerIndex: number | null;
  }) => {
    if (interactionId && questions.length && interactionParam) {
      try {
        await execUpdateInteraction(interactionParam);

        if (savingAnswerIndex != null) {
          setSavingFailed(false);
          if (
            (isBeingEdited && editQuestionIndex == currentQuestionIndex)
            || !isBeingEdited
          ) {
            // If I am saving the last visible question, I need to advance to the next question.
            // If I am saving an older question, I don't need to advance.
            advanceToNextQuestion();
          }

          clearEditData();
          setSavingAnswerIndex(null);
        }
      }
      catch {
        if (savingAnswerIndex) {
          setSavingFailed(true);
        }
        setRetrySavingModalOpen(true);
      }
    }
  };

  // Whenever the interaction is updated, we want to save the interaction.
  useEffect(() => {
    queueInteractionUpdate();
  }, [interaction, interactionId]);

  // Whenever the current question changes, we want to append the a new entry to the questionViewLog.
  useEffect(() => {
    if (interactionId == null || interaction == null) return;

    setInteraction((interaction) => {
      const lastViewedQuestion = isBeingEdited
        ? questions[editQuestionIndex]
        : questions[currentQuestionIndex];

      if (!lastViewedQuestion) return interaction;

      if ((interaction?.questionViewLog ?? []).length > 0) {
        const lastRecordedQuestion
          = (interaction!.questionViewLog ?? [])[((interaction!.questionViewLog ?? []).length - 1)];
        if (
          lastRecordedQuestion.action == ViewLogActionEnum.edit
          && lastRecordedQuestion.questionId == lastViewedQuestion.id
        ) {
          // We are still seeing the same question, no need to log it again.
          return interaction;
        }
      }

      const questionToLog: ViewLogQuestion = {
        action: isBeingEdited ? ViewLogActionEnum.edit : ViewLogActionEnum.new,
        questionId: lastViewedQuestion.id,
        title: lastViewedQuestion.title,
        text: lastViewedQuestion.text,
        timestamp: new Date(),
      };

      const viewLog = [...(interaction?.questionViewLog || []), questionToLog];

      return {
        ...interaction,
        editStatus: interaction?.editStatus || InteractionEditStatusEnum.open,
        responses: interaction?.responses || [],
        questionViewLog: viewLog,
        expired: interaction?.expired ?? false, // Ensure expired is always a boolean
      };
    });
  }, [isBeingEdited, editQuestionIndex, currentQuestionIndex, interactionId]);

  // Scroll to the feedback survey or end of survey message when they render.
  useEffect(() => {
    if (endOfQuestionnaire && surveyCompletedRef?.current) {
      debouncedScrollIntoView(surveyCompletedRef.current);
    }
  }, [endOfQuestionnaire]);

  const [clearQuestionsModalOpen, setClearQuestionsModalOpen] = useState(false);
  const [retrySavingModalOpen, setRetrySavingModalOpen] = useState(false);

  const clearEditData = () => {
    setEditQuestionId(0);
    setEditQuestionType('');
    setIsBeingEdited(false);
    setClearQuestions(false);
  };

  const closeClearQuestionsModal = () => {
    clearEditData();
    setClearQuestionsModalOpen(false);
  };

  const handleClearQuestions = () => {
    setClearQuestionsModalOpen(false);
    setClearQuestions(true);
  };

  const onShowFailureModal = () => {
    setRetrySavingModalOpen(true);
  };

  const retrySaving = () => {
    setRetrySavingModalOpen(false);
    queueInteractionUpdate();
  };

  const closeRetrySavingModal = () => {
    setRetrySavingModalOpen(false);
  };

  useEffect(() => {
    if (isLoading || isRestarting) return;

    if (
      interaction?.responses.find(x => x.questionId === currentQuestion.id)
    ) {
      // If the currentQuestion has an answer was recorded already, that means we are loading from the database.
      // We need to advance to the next question.
      advanceToNextQuestion();
    }
    else if (currentQuestion?.type === 'statement' && interactionId) {
      // If the current question is a statement, we can update the interaction because there is no user action required.
      recordAnswer({ questionId: currentQuestion.id, answers: [] });
    }
  }, [currentQuestionIndex, interactionId, isLoading, isRestarting]);

  // Single select has no 'continue' or 'submit' button outside the component like the other types,
  // so we need to listen here for a change from singleSelectResponse atom to execute the recordAnswer function.
  useEffect(() => {
    if (isLoading) return;
    if (endOfQuestionnaire) return;
    if (singleSelectResponse?.answers?.length) {
      recordAnswer(singleSelectResponse);
      setSingleSelectResponse({ questionId: 0, answers: [] });
    }
  }, [singleSelectResponse]);

  const completeQuestionnaire = () => {
    setInteraction((interaction) => {
      return {
        ...interaction!,
        editStatus: InteractionEditStatusEnum.closed,
      };
    });
    setEndOfQuestionnaire(true);
    // Show feedback survey only if interaction was not previously closed.
    if (!wasLoadedClosed) {
      setTimeout(() => {
        setDisplayFeedbackSurvey(true);
      }, 1500);
    }
  };

  const recordAnswer = (response: UserResponseType, skipped = false) => {
    const questionToRecord = questionsById.get(response.questionId!);

    if (!questionToRecord) {
      throw new Error('Something went wrong. The question does not exist.');
    }

    const questionResponse = {
      questionId: questionToRecord.id,
      title: questionToRecord.title,
      skipped,
      text: questionToRecord.text,
      type: questionToRecord.type,
      answers: response.answers,
    };

    const surveyResponse = interaction!;

    if (editQuestionId) {
      const oldQIndex = interaction!.responses.findIndex(
        question => question.questionId === response.questionId,
      );
      // SUBMIT EDITED ANSWER
      if (oldQIndex !== -1) {
        // answeredQuestions contains all the data from each individual question,
        // whereas surveyResponse is just what we will be sending in our patch request.
        // surveyResponse does not need the additional question data, just questionId, text and answers.
        const surveyRes = surveyResponse;

        if (clearQuestions) {
          const surveyResBeforeEdit = surveyRes.responses.slice(
            0,
            oldQIndex + 1,
          );
          surveyRes.responses = surveyResBeforeEdit;
          // user edited an unlocking/locking question so we need to go back to the editQuestionIndex
          setCurrentQuestionIndex(editQuestionIndex);
        }

        surveyRes.responses[oldQIndex] = questionResponse;
        setInteraction((interaction) => {
          return {
            ...interaction!,
            responses: surveyRes.responses,
          };
        });

        setSavingAnswerIndex(oldQIndex);
      }
    }
    // SUBMIT NON-EDITED ANSWER
    else {
      surveyResponse.responses.push(questionResponse);

      if (interactionId)
        setInteraction((interaction) => {
          return {
            ...interaction!,
            responses: surveyResponse.responses,
          };
        });

      setSavingAnswerIndex(surveyResponse.responses.length - 1);
    }
  };

  const recordFeedback = (response: UserResponseType) => {
    if (!feedbackSurveyData) return;
    const questionToRecord = feedbackQuestionsById.get(response.questionId);

    if (questionToRecord) {
      const questionResponse = {
        questionId: questionToRecord?.id,
        title: questionToRecord?.title,
        text: questionToRecord?.text,
        answers: response.answers,
      };
      const feedbackSurveyResponse = updatedFeedbackSurveyResponse;
      const allAnsweredQuestions = [
        ...answeredFeedbackQuestions,
        { ...questionToRecord, userResponse: response },
      ];
      feedbackSurveyResponse.responses.push(questionResponse);
      setUpdatedFeedbackSurveyResponse(feedbackSurveyResponse);
      setAnsweredFeedbackQuestions([...allAnsweredQuestions]);
    }
  };

  const skipQuestion = () => {
    const skippedQuestion = {
      questionId: currentQuestion.id,
      answers: [{ text: 'Question skipped' }],
    };
    const skipped = true;
    recordAnswer(skippedQuestion, skipped);
  };

  const advanceToNextQuestion = () => {
    if (!interaction!.responses?.length) return;

    const advanceCurrentQuestionIndex = () => {
      let newIndex = null;

      for (let i = currentQuestionIndex + 1; i < questions.length; i++) {
        const questionNotUnlocked = !unlockedUuids?.has(questions[i].uuid);

        const questionAvailable = !(questions[i].locked && questionNotUnlocked);
        if (questionAvailable) {
          newIndex = i;
          break;
        }
      }

      if (newIndex == null) {
        completeQuestionnaire();
      }
      else {
        setCurrentQuestionIndex(newIndex);
      }
    };
    const lastAnswered
      = interaction!.responses[interaction!.responses.length - 1];
    // To prevent too many questions showing up at the same time, we use this logic to delay the function
    // that triggers the currentQuestionIndex changing. This way we can show a response after the user answers a question,
    // and then 1.5 seconds later we can display the next question.

    const hasResponse = !!questionsById
      .get(lastAnswered.questionId)
      ?.answers.some(
        selection =>
          lastAnswered.answers.map(x => x.id).includes(selection.id)
          && selection?.response,
      );

    if (
      !isBeingEdited
      && (questionsById.get(lastAnswered.questionId)?.type === 'statement'
        || questions[currentQuestionIndex]?.type == 'statement'
        || hasResponse)
    ) {
      // if the type is multiselect, this prevents the answer footer from persisting during the 1.5 seconds while we show the responses.
      setHideAnswerFooter(true);
      setDisableEditButtons(true);

      setTimeout(() => {
        advanceCurrentQuestionIndex();
        setDisableEditButtons(false);
        setHideAnswerFooter(false);
      }, 1500);
    }
    else {
      advanceCurrentQuestionIndex();
    }
  };

  // If the chatbot has multiple questions/statements to display in a row (with no user interaction in between them),
  // we want the avatar to only show up for its first message, and be hidden from its subsequent messages.
  // So if the previous question is not a statement, and that question does not have an answer response, then show the Avatar.
  const shouldDisplayAvatar = (prevQuestion: InteractionUserResponses) => {
    const previousAnswerHasNoResponse = !prevQuestion?.answers?.some(
      answer => answer.response,
    );
    const previousTypeIsNotAStatement
      = questionsById.get(prevQuestion?.questionId)?.type !== 'statement';

    return previousTypeIsNotAStatement && previousAnswerHasNoResponse;
  };

  const renderCurrentQuestion = () => {
    if (endOfQuestionnaire) return;
    if (savingAnswerIndex !== null) return;
    if (currentQuestionIndex > questions.length - 1) return;
    if (
      (!isBeingEdited
        && interaction!.responses.some(
          answer => answer.questionId === currentQuestion.id,
        ))
        || isBeingEdited
    )
      return;

    const showAvatar = shouldDisplayAvatar(
      interaction!.responses?.[interaction!.responses.length - 1],
    );

    return (
      <div style={{ display: 'flex', marginTop: showAvatar ? '20px' : '10px' }}>
        <div style={{ marginRight: '12px' }}>
          <Avatar showAvatar={showAvatar} />
        </div>
        <div style={{ width: '100%' }}>
          <QuestionCard
            {...currentQuestion}
            disabled={interactionExpirationDialog}
            isBeingEdited={false}
            isAnswered={false}
            isActiveQuestion={true}
          />
          {!interactionExpirationDialog
            && !currentQuestion.required
            && currentQuestion.type !== 'statement' && (
            <Button
              style={{
                textAlign: 'left',
                color: '#2974a8',
                marginTop: '10px',
                padding: 0,
              }}
              type="link"
              onClick={skipQuestion}
              data-cy="skip_btn"
            >
              Skip Question
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderAnsweredQuestionsAndResponses = () => {
    if (!interaction) return;

    return interaction.responses?.map((question, i) => {
      const answers = question.answers || [];
      const answerText = answers.map(answer => answer.text).join(', ');
      const editQIndex = questions.findIndex(
        answeredQs => answeredQs.id === question.questionId,
      );

      const questionToEdit = questions.find(
        q => q.id === question.questionId,
      );

      const unlockingQuestion = questionToEdit?.answers.some(
        ans => ans?.unlocksQuestionUuids?.length,
      );

      const surveyQuestion = questionsById.get(question.questionId);

      if (!surveyQuestion) {
        throw new Error('Something went wrong. The question does not exist.');
      }

      const renderResponse = answers.map((answer) => {
        const surveyAnswer = surveyQuestion?.answers.find(
          a => a.id === answer.id,
        );

        if (!surveyAnswer?.response) return;
        // TODO: This is a hack to reuse the QuestionCard component. We should create an actual response card component, or
        // create a generic card component that can be used for both questions and responses.
        // The only thing we care about in responseQuestionProps is the text field, because this is what will show up on the UI.
        // The rest of these props are only necessary because TypeScript is expecting them in the QuestionCard. The values don't matter.
        const responseQuestionProps = {
          id: i + 100,
          uuid: `${i + 100}`,
          text: surveyAnswer?.response,
          index: interaction!.responses.length + 100,
          type: 'statement',
          disabled: interactionExpirationDialog,
          answers: [],
        };
        return (
          <div
            key={answer.id}
            style={{
              display: 'flex',
              marginTop: '20px',
            }}
          >
            <div style={{ marginRight: '12px' }}>
              {/* Because the response will always show up before subsequent questions, the avatar should always be present */}
              <Avatar showAvatar={true} />
            </div>
            <QuestionCard
              {...responseQuestionProps}
              isBeingEdited={false}
              isAnswered={false}
              isActiveQuestion={false}
            />
          </div>
        );
      });

      const onEdit = () => {
        setIsBeingEdited(true);
        setEditQuestionType(questions[editQIndex].type);
        setEditQuestionId(question.questionId!);
        setEditQuestionIndex(editQIndex);
        if (unlockingQuestion) {
          setClearQuestionsModalOpen(true);
        }
      };

      const showAvatar = shouldDisplayAvatar(interaction!.responses?.[i - 1]);
      const savingCurrentAnswer = savingAnswerIndex == i;
      const savingCurrentAnswerFailed = savingCurrentAnswer && savingFailed;

      return (
        <div key={question.questionId}>
          <div
            style={{
              display: 'flex',
              marginTop: showAvatar ? (i !== 0 ? '20px' : 0) : '10px',
            }}
            ref={currentQuestionRef}
          >
            <div style={{ marginRight: '12px' }}>
              <Avatar showAvatar={showAvatar} />
            </div>
            <QuestionCard
              text={question.text!}
              id={question.questionId!}
              uuid={surveyQuestion.uuid!}
              type={surveyQuestion.type!}
              index={surveyQuestion.index}
              answers={surveyQuestion.answers!}
              isAnswered={question.answers?.length ? true : false}
              selectedAnswers={question.answers}
              isBeingEdited={editQuestionId === question.questionId}
              isActiveQuestion={editQuestionId === question.questionId}
              disabled={interactionExpirationDialog}
            />
          </div>
          {!!answers?.length && (
            <Row justify="end">
              <Col
                style={{
                  maxWidth: '75%',
                  marginTop: '20px',
                }}
              >
                <Space>
                  {savingCurrentAnswer
                    ? (
                        savingCurrentAnswerFailed && (
                          <Button
                            type="default"
                            shape="circle"
                            icon={<WarningOutlined />}
                            size="middle"
                            onClick={onShowFailureModal}
                            style={{
                              background: COLORS.tundoraL3,
                              border: 'none',
                              color: '#cc0000',
                            }}
                            data-cy={`error_${question.questionId}`}
                          />
                        )
                      )
                    : !endOfQuestionnaire
                        ? (
                            <Button
                              type="default"
                              shape="circle"
                              onClick={onEdit}
                              icon={<EditOutlined />}
                              disabled={
                                disableEditButtons
                                || question?.submitted
                                || interactionExpirationDialog
                              }
                              size="middle"
                              style={{
                                background: COLORS.tundoraL3,
                                border: 'none',
                                color:
                          disableEditButtons || question?.submitted
                            ? COLORS.tundoraL2
                            : COLORS.tundoraL0,
                              }}
                              data-cy={`edit_button_${question.questionId}`}
                              data-cy-question-index={surveyQuestion.index}
                              data-cy-element-type="editButton"
                            />
                          )
                        : (
                            <div style={{ width: '32px' }} />
                          )}
                  <UserResponse questionIndex={surveyQuestion.index} key={answerText} text={answerText} ref={ref} />
                </Space>
              </Col>
            </Row>
          )}
          {renderResponse}
        </div>
      );
    });
  };

  // State to control the visibility of the resume or reset modal.
  const [interactionExpirationDialog, setInteractionExpirationDialog]
    = useState(false);

  const handleStartFromBeginning = async () => {
    setIsRestarting(true);
    setInteractionExpirationDialog(false);
    setInteraction(null);
    setCurrentQuestionIndex(0);

    // Create a new interaction using the existing script
    if (interaction) {
      let result: CreateInteractionResponseType;
      if (isStaffInteraction && staffMemberId) {
        result = await createStaffInteraction({
          scriptId: questionnaireData.id,
          staffMemberId,
        });
      }
      else if (!isStaffInteraction && patientId && encounterId && unitId) {
        result = await createPatientInteraction({
          scriptId: questionnaireData.id,
          patientId,
          encounterId,
          unitId,
        });
      }
      else {
        return;
      }

      const newInteraction = {
        id: result.id,
        questionViewLog: [],
        expired: false,
        editStatus: InteractionEditStatusEnum.open,
        responses: [],
      };

      // Update the state
      setInteraction(newInteraction);
      setInteractionId(newInteraction.id);
      updateInteractionIdInURL(newInteraction.id);
    }
  };

  // Watches for the new interaction to be set before we restart
  useEffect(() => {
    if (isRestarting && interaction && interaction.id) {
      setIsRestarting(false);
    }
  }, [interaction, isRestarting]);

  // Renders the component if the loaded survey interaction is expired.
  // Gives user the option to continue or start the survey from the beginning.
  const renderInteractionExpirationDialog = () => {
    if (interactionExpirationDialog) {
      return (
        <InteractionsExpirationDialog
          onContinue={() => {
            const int = {
              questionViewLog: interaction?.questionViewLog || [],
              responses: interaction?.responses || [],
              editStatus: InteractionEditStatusEnum.open,
              expired: false,
            };
            setInteraction(int);
            setInteractionExpirationDialog(false);
          }}
          onStartFromBeginning={() => {
            handleStartFromBeginning();
          }}
        />
      );
    }
  };

  if (!interaction || isLoading) return null;

  return (
    <Layout
      style={{
        minHeight: '100vh',
      }}
    >
      {errorLoadingData && (
        <ErrorAlert message="There was an error loading your information. Your responses may not be recorded. Please try again later." />
      )}

      <RetrySavingModal
        isOpen={retrySavingModalOpen}
        handleRetry={retrySaving}
        handleCancel={closeRetrySavingModal}
      />

      <ClearQuestionsModal
        isOpen={clearQuestionsModalOpen}
        handleOk={handleClearQuestions}
        handleCancel={closeClearQuestionsModal}
      />

      <Row>
        <Col
          span={24}
          style={{ display: 'flex', padding: '20px', flexDirection: 'column' }}
        >
          <div>{renderAnsweredQuestionsAndResponses()}</div>

          {renderCurrentQuestion()}
          {renderInteractionExpirationDialog()}
          {displayFeedbackSurvey && feedbackSurveyData && !wasLoadedClosed && (
            <div style={{ marginTop: '20px' }} ref={surveyCompletedRef}>
              <div
                style={{
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                  fontSize: '14px',
                }}
              >
                <FeedbackSurvey
                  feedbackSurveyData={feedbackSurveyData}
                  token={token}
                  recordFeedback={recordFeedback}
                />
              </div>
            </div>
          )}
          {endOfQuestionnaire && (!feedbackSurveyData || wasLoadedClosed) && (
            <div ref={surveyCompletedRef}>
              <EndOfSurveyMessage isStaffInteraction={isStaffInteraction} />
            </div>
          )}
          {!questions.length && (
            <Space direction="vertical">
              <ErrorAlert message="No questions available. Please try again later." />
              <Link
                data-cy="landing-page-redirect"
                to="../../landing-page"
                relative="path"
              >
                Tap here to return to home page.
              </Link>
            </Space>
          )}
        </Col>
      </Row>
      {currentQuestion?.id
        && !endOfQuestionnaire
        && !hideAnswerFooter
        && !savingAnswerIndex && (
        <AnswerFooter
          type={editQuestionType || currentQuestion.type}
          questionId={editQuestionId || currentQuestion.id}
          recordAnswer={recordAnswer}
          isBeingEdited={isBeingEdited}
          disabled={interactionExpirationDialog}
        />
      )}
      {endOfQuestionnaire
        && feedbackSurveyData
        && !feedbackSurveyComplete
        && !savingAnswerIndex && (
        <AnswerFooter
          type={
            feedbackSurveyData.questions[currentFeedbackQuestionIndex].type
          }
          questionId={
            feedbackSurveyData.questions[currentFeedbackQuestionIndex].id
          }
          recordAnswer={recordFeedback}
          isBeingEdited={false}
        />
      )}
    </Layout>
  );
};

export default Questionnaire;
